// src/components/NavigationBar.js
import React, { useState, useEffect, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Button, Box, IconButton, Avatar, Popover, Typography,
  Divider, CircularProgress, Drawer, List, ListItem, ListItemText,
  useMediaQuery, Menu, MenuItem, Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api from '../api';

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isSmallDesktop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const showHamburgerMenu = useMediaQuery(theme.breakpoints.down('lg'));

  // ---- State ----
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMastersOpen, setMobileMastersOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [mastersAnchorEl, setMastersAnchorEl] = useState(null);

  // Dynamic Meeting Access State
  const [isMeetingOrganizer, setIsMeetingOrganizer] = useState(false);

  // ---- LocalStorage / User ----
  const currentRole = localStorage.getItem('role') || 'Employee';
  const firstName = localStorage.getItem('first_name') || 'User';
  const rawEmpId = localStorage.getItem('emp_id') || '';
  const empId = rawEmpId.trim().toUpperCase();
  const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;
  const profilePicUrl = `${baseUrl}/employees/${empId}/profile-picture?t=${Date.now()}`;

  // ---- Meeting Organizer check ----
  useEffect(() => {
    if (['Admin', 'Super Admin', 'HR Manager', 'HR Executive'].includes(currentRole)) {
      setIsMeetingOrganizer(true);
    } else {
      api.get(`/meeting-views/is-organizer/${empId}`)
        .then(res => setIsMeetingOrganizer(res.data.is_organizer))
        .catch(() => setIsMeetingOrganizer(false));
    }
  }, [empId, currentRole]);

  // ---- Handlers ----
  const handleLogout = () => {
    localStorage.clear();
    setAnchorEl(null);
    navigate('/');
    window.location.reload();
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (!profileData) {
      setLoadingProfile(true);
      api.get(`/employee-card/${empId}`)
        .then(res => { setProfileData(res.data); setLoadingProfile(false); })
        .catch(err => {
          console.error('Failed to fetch profile popover data', err);
          setLoadingProfile(false);
        });
    }
  };

  const handleClosePopover = () => setAnchorEl(null);
  const openPopover = Boolean(anchorEl);
  const popoverId = openPopover ? 'profile-popover' : undefined;

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleNavClick = (path) => {
    if (path) { navigate(path); setMobileOpen(false); }
  };

  // ---- Active state logic ----
  const isActive = (path) => {
    if (!path) return false;
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (location.pathname === path) return true;
    if (location.pathname.startsWith(path + '/')) return true;
    return false;
  };

  const isItemActive = (item) => {
    if (item.path && isActive(item.path)) return true;
    if (item.children && item.children.some(child => isActive(child.path))) return true;
    return false;
  };

  // ---- Nav styling ----
  const navStyle = (item) => ({
    mx: 0.2,
    px: isSmallDesktop ? 0.8 : 1.2,
    py: 0.4,
    fontSize: isSmallDesktop ? '0.7rem' : '0.800rem',
    fontWeight: isItemActive(item) ? 700 : 500,
    color: 'inherit',
    bgcolor: isItemActive(item) ? 'primary.light' : 'transparent',
    borderRadius: 1.5,
    minHeight: isSmallDesktop ? '28px' : '36px',
    '&:hover': {
      bgcolor: isItemActive(item) ? 'primary.light' : 'rgba(255, 255, 255, 0.1)',
    },
  });

  // ---- Navigation items config ----
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', roles: ['all'] },
    { path: '/connect', label: 'Connect+', roles: ['all'] },
    { path: '/roster', label: 'Roster', roles: ['all'] },
    { path: '/leave', label: 'Leave', roles: ['all'] },
    { path: '/attendance', label: 'Attendance', roles: ['all'] },
    { path: '/meeting', label: 'Meeting', roles: ['MeetingOrganizer'] },
    {
      label: 'Masters',
      roles: ['Admin', 'Super Admin', 'HR Manager', 'HR Executive'],
      children: [
        { path: '/employees', label: 'Directory', roles: ['Admin', 'Super Admin', 'HR Manager', 'HR Executive'] },
        { path: '/hr/documents', label: 'Documents', roles: ['Admin', 'Super Admin', 'HR Manager', 'HR Executive'] },
        { path: '/leave-master', label: 'Leave Master', shortLabel: 'Lv. Master', roles: ['Admin', 'Super Admin', 'HR Manager', 'HR Executive'] },
        { path: '/attendance-master', label: 'Att. Master', shortLabel: 'At. Master', roles: ['Admin', 'Super Admin', 'HR Manager'] },
        { path: '/payroll', label: 'Payroll', roles: ['Admin', 'Super Admin', 'HR Manager'] },
      ],
    },
    { path: '/login-audit', label: 'Audit', roles: ['Admin', 'Super Admin'] },
    { path: '/settings', label: 'Settings', roles: ['Admin', 'Super Admin'] },
    { path: '/superadmin', label: 'Super Admin', roles: ['Super Admin'] },
  ];

  const getVisibleNavItems = () => {
    return navItems
      .map(item => {
        // Dynamic Meeting item
        if (item.roles.includes('MeetingOrganizer')) {
          return isMeetingOrganizer ? item : null;
        }

        // Items with children (Masters, etc.)
        if (item.children) {
          const visibleChildren = item.children.filter(
            child => child.roles.includes('all') || child.roles.includes(currentRole)
          );
          if (
            visibleChildren.length > 0 &&
            (item.roles.includes('all') || item.roles.includes(currentRole))
          ) {
            return { ...item, children: visibleChildren };
          }
          return null;
        }

        // Regular items
        if (item.roles.includes('all') || item.roles.includes(currentRole)) return item;
        return null;
      })
      .filter(Boolean);
  };

  const visibleNavItems = getVisibleNavItems();

  // ---- Mobile Drawer content ----
  const drawer = (
    <Box sx={{ width: 230, height: '100%', bgcolor: 'background.default' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <img
          src="/transact_people1.png"
          alt="Company Logo"
          style={{ height: '35px', objectFit: 'contain' }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/150x40?text=LOGO';
          }}
        />
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {visibleNavItems.map((item) => (
          <Fragment key={item.label}>
            <ListItem
              onClick={() =>
                item.children
                  ? setMobileMastersOpen(!mobileMastersOpen)
                  : handleNavClick(item.path)
              }
              sx={{
                bgcolor: isItemActive(item) ? 'primary.light' : 'transparent',
                '&:hover': {
                  bgcolor: isItemActive(item) ? 'primary.light' : 'rgba(0,0,0,0.04)',
                },
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                cursor: 'pointer',
              }}
            >
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: isItemActive(item) ? 700 : 500,
                    color: isItemActive(item) ? 'primary.main' : 'text.primary',
                  },
                }}
              />
              {item.children && (mobileMastersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </ListItem>

            {item.children && (
              <Collapse in={mobileMastersOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem
                      key={child.path}
                      onClick={() => handleNavClick(child.path)}
                      sx={{
                        pl: 4,
                        cursor: 'pointer',
                        bgcolor: isActive(child.path) ? 'primary.light' : 'transparent',
                        borderRadius: 1,
                        mx: 1,
                        mb: 0.5,
                      }}
                    >
                      <ListItemText
                        primary={child.shortLabel || child.label}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: isActive(child.path) ? 700 : 500,
                            color: isActive(child.path) ? 'primary.main' : 'text.primary',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'primary.main',
          '& .MuiToolbar-root': {
            minHeight: isMobile ? '38px' : isTablet ? '52px' : isSmallDesktop ? '56px' : '54px',
            padding: isMobile ? '0 8px' : '0 16px',
          },
        }}
      >
        <Toolbar>
          {showHamburgerMenu ? (
            // ---- Mobile / Tablet / Small Desktop ----
            <>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: isMobile ? 1 : 2, padding: isMobile ? '6px' : '8px' }}
              >
                <MenuIcon sx={{ fontSize: isMobile ? '20px' : '24px' }} />
              </IconButton>

              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img
                  src="/transact_people1.png"
                  alt="Company Logo"
                  style={{
                    height: isMobile ? '28px' : isTablet ? '32px' : '35px',
                    objectFit: 'contain',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/dashboard')}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/150x40?text=LOGO';
                  }}
                />
              </Box>

              <IconButton
                onClick={handleProfileClick}
                sx={{
                  ml: 1,
                  p: 0,
                  '& .MuiAvatar-root': {
                    width: isMobile ? 28 : 32,
                    height: isMobile ? 28 : 32,
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                  },
                }}
                aria-describedby={popoverId}
              >
                <Avatar
                  src={profilePicUrl}
                  sx={{
                    bgcolor: '#ffb300',
                    fontWeight: 'bold',
                    border: '2px solid rgba(255,255,255,0.8)',
                  }}
                >
                  {firstName.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </>
          ) : (
            // ---- Desktop Layout (> 1200px) ----
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <img
                  src="/transact_people1.png"
                  alt="Company Logo"
                  style={{ height: '40px', objectFit: 'contain', cursor: 'pointer' }}
                  onClick={() => navigate('/dashboard')}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/150x40?text=LOGO';
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                {visibleNavItems.map((item) => (
                  <Fragment key={item.label}>
                    {item.children ? (
                      <>
                        <Button
                          sx={navStyle(item)}
                          onClick={(e) => setMastersAnchorEl(e.currentTarget)}
                          endIcon={<ExpandMoreIcon />}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          anchorEl={mastersAnchorEl}
                          open={Boolean(mastersAnchorEl)}
                          onClose={() => setMastersAnchorEl(null)}
                          PaperProps={{
                            sx: { mt: 1.5, minWidth: 180, borderRadius: 2 },
                          }}
                        >
                          {item.children.map(child => (
                            <MenuItem
                              key={child.path}
                              component={Link}
                              to={child.path}
                              onClick={() => setMastersAnchorEl(null)}
                              selected={isActive(child.path)}
                            >
                              {child.shortLabel || child.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      <Button component={Link} to={item.path} sx={navStyle(item)}>
                        {item.shortLabel || item.label}
                      </Button>
                    )}
                  </Fragment>
                ))}

                <IconButton
                  onClick={handleProfileClick}
                  sx={{ ml: 2, p: 0 }}
                  aria-describedby={popoverId}
                >
                  <Avatar
                    src={profilePicUrl}
                    sx={{
                      bgcolor: '#ffb300',
                      width: 36,
                      height: 36,
                      fontWeight: 'bold',
                      border: '2px solid rgba(255,255,255,0.8)',
                    }}
                  >
                    {firstName.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* ---- Mobile Drawer ---- */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 230 },
        }}
      >
        {drawer}
      </Drawer>

      {/* ---- Profile Popover ---- */}
      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header: Logo & Sign out */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 1.5 }}>
          <img
            src="/transact_people.png"
            alt="Logo"
            style={{ height: '35px', objectFit: 'contain' }}
            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
          />
          <Typography
            variant="body2"
            onClick={handleLogout}
            sx={{
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
            }}
          >
            Sign out
          </Typography>
        </Box>

        {/* Body: Profile Picture & Details */}
        <Box sx={{ display: 'flex', p: 2, pt: 1, gap: 2.5 }}>
          <Avatar
            src={profilePicUrl}
            sx={{ width: 88, height: 88, bgcolor: 'primary.light', fontSize: '2rem' }}
          >
            {firstName.charAt(0).toUpperCase()}
          </Avatar>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flexGrow: 1,
              overflow: 'hidden',
            }}
          >
            {loadingProfile ? (
              <CircularProgress size={24} sx={{ mt: 2, ml: 2 }} />
            ) : profileData ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5, color: '#202124' }}>
                  {profileData.first_name} {profileData.last_name}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {profileData.email}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.5 }}>
                  {profileData.designation}
                </Typography>

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {profileData.department} | {profileData.emp_id}
                </Typography>

                <Typography
                  variant="body2"
                  onClick={() => { setAnchorEl(null); navigate('/my-profile'); }}
                  sx={{
                    color: 'primary.main',
                    mt: 1.5,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  My account
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="error">Failed to load profile.</Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Footer / Extensible Area */}
        <Box sx={{ p: 1.5, bgcolor: '#f8f9fa' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#e8eaed' },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'transparent',
                border: '1px solid #dadce0',
                color: '#5f6368',
              }}
            >
              <svg
                focusable="false"
                aria-hidden="true"
                viewBox="0 0 24 24"
                data-testid="PersonAddAltIcon"
                style={{ width: '18px', height: '18px' }}
              >
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
              </svg>
            </Avatar>
            <Typography variant="body2" sx={{ color: '#3c4043' }}>
              Sign in with a different account
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default NavigationBar;